import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { Injector } from '@angular/core'  
import { BehaviorSubject, Observable, catchError, forkJoin, of, throwError } from 'rxjs';
import { PasseLivreService } from 'src/app/core/services/passe-livre/passe-livre.service';
import { FlightResponse } from 'src/app/flight/models/FlightResponse';
import { FlightsFormData } from 'src/app/flight/models/FlightsFormData';
import { Direction } from 'src/app/flight/models/Direction';
import { setItemInSessionStorage, FLIGHT_FORM_DATA, CHOOSE_FLIGHT_DATA, removeItemFromSessionStorage } from 'src/app/utils/SessionStorageUtil';
import { formatDate, formatDateToStandard } from 'src/app/utils/DateUtil';
import { assign } from 'lodash';
import { RequestStatus } from 'src/app/models/RequestStatus';
import { MatDialog } from '@angular/material/dialog';
import { DialogInfoComponent } from 'src/app/shared/components/dialog-info/dialog-info.component';
import { TabsMainComponent } from '../tabs-main/tabs-main.component';
import { AuthorizationService } from 'src/app/core/services/authorization/authorization.service';
import { PasseLivreLatamCrewService } from 'src/app/core/services/passe-livre/passe-livre-latam-crew.service';
import { PasseLivreExternalCrewService } from 'src/app/core/services/passe-livre/passe-livre-external-crew.service';
import { FlightSegmentSch, OTAAirScheduleResponse, OriginDestinationOption } from '../models/OTAAirScheduleResponse';
import { UiService } from 'src/app/shared/services/ui.service';
import { SeatCountResponse, SeatCountFlight } from 'src/app/flight/models/SeatCountResponse';

@Component({
    selector: 'app-new-reservation',
    templateUrl: './new-reservation.component.html',
    styleUrls: ['./new-reservation.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class NewReservationComponent {

  Direction = Direction;

  flightsForm: FormGroup = new FormGroup({});
  chooseFlightForm: FormGroup = new FormGroup({});
  
  flightsFormData: FlightsFormData = new FlightsFormData();
  closePanelObservable: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  showChooseFlight = false;
  proceeded = false;
  flightsRequestStatus: RequestStatus = new RequestStatus();

  outboundFlightsxc: OTAAirScheduleResponse;
  inboundFlightsxc: OTAAirScheduleResponse;

  outboundFlights: OTAAirScheduleResponse[] = [];
  inboundFlights: OTAAirScheduleResponse[] = [];

  private passeLivreService: PasseLivreService;

  constructor(private changeDetector: ChangeDetectorRef, 
              private router: Router,
              private dialog: MatDialog,
              @Inject(TabsMainComponent) private tabsMain: TabsMainComponent,
              private injector: Injector,
              private authorizationService: AuthorizationService,
              private uiService: UiService) {

    if (this.authorizationService.isExternal()) {
      this.passeLivreService = <PasseLivreExternalCrewService>this.injector.get(PasseLivreExternalCrewService);
    } else {
      this.passeLivreService = <PasseLivreLatamCrewService>this.injector.get(PasseLivreLatamCrewService);
    }


  }

  searchFlights(): void {
      if (this.flightsForm.valid) {
        this.getFlights();

        setItemInSessionStorage(FLIGHT_FORM_DATA, this.flightsForm.getRawValue());
        removeItemFromSessionStorage(CHOOSE_FLIGHT_DATA);
        
        this.proceeded = false;
        this.chooseFlightForm.reset();
        this.closePanelObservable.next(true);
    }    
  }

  getFlights(): void {
    if (this.flightsForm.valid) {

      this.showChooseFlight = true;
      assign(this.flightsFormData, this.flightsForm.getRawValue());
      this.changeDetector.markForCheck();

      this.flightsRequestStatus.resetValues();
      this.flightsRequestStatus.loading = true;

      // validate showloading
      this.uiService.setLoading(true);
      
      forkJoin(this.createRequestsFlights()).subscribe({
        next: (values: OTAAirScheduleResponse[][]) => {
          this.outboundFlights = this.orderFlights(values[0]);
          this.outboundFlightsxc = this.outboundFlights[0];
          
          if (values[1]) {
            this.inboundFlights = this.orderFlights(values[1]);
            this.inboundFlightsxc = this.inboundFlights[0];
          }
          this.prepareSeatCountsLoading();

          // Desde este punto las tarjetas ya pueden renderizarse.
          this.uiService.setLoading(false);
        
          this.loadSeatCounts();

        },
        error: (error) => {
          this.showChooseFlight = false;
          this.flightsRequestStatus.error = true;

          this.outboundFlights = [];
          this.inboundFlights = [];

          if (error.status === 406) {
            const html: string = `<span class="error-message">${error.error}</span>`;

            this.dialog.open(DialogInfoComponent, {
              width: '600px',
              data: { title: 'Ocorreu um erro!', html }
            }).afterClosed().subscribe(() => {
              this.tabsMain.select(0);
            });

            this.flightsRequestStatus.setErrorMessage('');
          } else {
            this.flightsRequestStatus.setErrorMessage(error.error);
          }

          this.uiService.setLoading(false);
        }
      }).add(() => {
        this.flightsRequestStatus.loading = false;
        this.changeDetector.markForCheck();
      });
    }


  }

  private prepareSeatCountsLoading(): void {
    const isExternal =
      this.authorizationService.isExternal();
  
    const allOptions = [
      ...(this.outboundFlightsxc
        ?.destinationOptionList ?? []),
      ...(this.inboundFlightsxc
        ?.destinationOptionList ?? [])
    ];
  
    for (const option of allOptions) {
      for (const segment of option.flightSegments) {
        // Fuerza el estado visual "...", incluso si /flights
        // envía availableSeats: null.
        segment.availableSeats = undefined;
  
        // Los externos no pueden seleccionar el vuelo
        // hasta conocer su disponibilidad real.
        if (isExternal) {
          segment.blocked = true;
        }
      }
    }
  }

  /**
   * Recopila segmentos únicos (por flightNumber|origin|fecha) de ida y vuelta,
   * pide el conteo en lote al BL y mapea la respuesta sobre cada FlightSegmentSch.
   *
   * Para LATAM: siempre se muestra totalAvailableSeats (número real o "—" si falla).
   * Para crew externo: idem — el campo `blocked` proviene del GET /flights y no se modifica.
   */
  private loadSeatCounts(): void {
    const allSegments = this.collectAllSegments();
    if (allSegments.length === 0) return;

    this.passeLivreService.getSeatCounts(allSegments).subscribe({
      next: (response: SeatCountResponse) => {
        this.mapSeatCountsToSegments(response.flights);
        this.changeDetector.markForCheck();
      },
      error: () => {
        // Fallo total de red: marcar todos los segmentos como fallidos
        this.markAllSegmentsFailed();
        this.changeDetector.markForCheck();
      }
    });
  }

  /**
   * Devuelve lista de segmentos únicos de ida y vuelta.
   * Extrae la fecha pura (yyyy-MM-dd) del campo departureDateTime (formato "yyyy-MM-dd HH:mm").
   */
  private collectAllSegments(): Array<{ flightNumber: string; origin: string; departureDate: string }> {
    const seen = new Set<string>();
    const result: Array<{ flightNumber: string; origin: string; departureDate: string }> = [];

    const allOptions = [
      ...(this.outboundFlightsxc?.destinationOptionList ?? []),
      ...(this.inboundFlightsxc?.destinationOptionList ?? [])
    ];

    for (const option of allOptions) {
      for (const seg of option.flightSegments) {
        const departureDate = seg.departureDateTime?.substring(0, 10) ?? '';
        const key = `${seg.flightNumber}|${seg.originLocationCode}|${departureDate}`;
        if (!seen.has(key)) {
          seen.add(key);
          result.push({
            flightNumber: seg.flightNumber,
            origin: seg.originLocationCode,
            departureDate
          });
        }
      }
    }
    return result;
  }

  /**
   * Mapea los conteos recibidos sobre cada FlightSegmentSch.
   * Segmentos sin match en la respuesta quedan con availableSeats = null ("—").
   */
  private mapSeatCountsToSegments(flights: SeatCountFlight[]): void {
    const countMap = new Map<string, number | null>();
    for (const f of flights) {
      const key = `${f.flightNumber}|${f.origin}|${f.departureDate}`;
      countMap.set(key, f.totalAvailableSeats);
    }

    const allOptions = [
      ...(this.outboundFlightsxc?.destinationOptionList ?? []),
      ...(this.inboundFlightsxc?.destinationOptionList ?? [])
    ];

    for (const option of allOptions) {
      for (const seg of option.flightSegments) {
        const departureDate = seg.departureDateTime?.substring(0, 10) ?? '';
        const key = `${seg.flightNumber}|${seg.originLocationCode}|${departureDate}`;
        seg.availableSeats = countMap.has(key) ? countMap.get(key)! : null;

        if (this.authorizationService.isExternal()) {
          seg.blocked =
            seg.availableSeats === null ||
            seg.availableSeats <= 0;
        }
      }
    }
  }

  /** Marca todos los segmentos como fallidos (null → la card muestra "—"). */
  private markAllSegmentsFailed(): void {
    const allOptions = [
      ...(this.outboundFlightsxc?.destinationOptionList ?? []),
      ...(this.inboundFlightsxc?.destinationOptionList ?? [])
    ];
    for (const option of allOptions) {
      for (const seg of option.flightSegments) {
        seg.availableSeats = null;
        if (this.authorizationService.isExternal()) {
          seg.blocked = true;
        }
      }
    }
  }

  private createRequestsFlights(): Observable<OTAAirScheduleResponse[]>[] {
    const requests: Observable<OTAAirScheduleResponse[]>[] = [];

    const { airportOrigin, airportDestiny } = this.flightsFormData;
    const dateTimeClient = formatDate(new Date(), 'YYYY-MM-DD HH:mm');
    const dtDeparture = formatDateToStandard(this.flightsFormData.dtDeparture);
    const outbound$ = this.passeLivreService.getFlightsWithConex(dtDeparture, dateTimeClient, airportOrigin, airportDestiny);
    requests.push(outbound$);

    if (this.flightsFormData.isRoundTrip()) {
      const dtArrival = formatDateToStandard(this.flightsFormData.dtArrival);
      const inbound$ = this.passeLivreService.getFlightsWithConex(dtArrival, dateTimeClient, airportDestiny, airportOrigin);
      requests.push(inbound$);
    }
    return requests;
  
  }


  private orderFlights(flights: OTAAirScheduleResponse | OTAAirScheduleResponse[]): OTAAirScheduleResponse[] {
  
    if (Array.isArray(flights)) {
       flights.forEach(otaAirScheduleResponse => {
        this.orderFlightSegments(otaAirScheduleResponse);
      });
  
      return flights;
    } else if (typeof flights === 'object') {
      this.orderFlightSegments(flights);
      return [flights];
    } else {
      console.error('flights no es un array ni un objeto válido');
      return [];
    }
  }
  
  private orderFlightSegments(otaAirScheduleResponse: OTAAirScheduleResponse): void {
    if (!otaAirScheduleResponse.destinationOptionList || !otaAirScheduleResponse.destinationOptionList.length) {
      console.error('destinationOptionList vacío o indefinido');
      return;
    }
  
    otaAirScheduleResponse.destinationOptionList.forEach(option => {
      option.flightSegments.sort((a: FlightSegmentSch, b: FlightSegmentSch) => {
        const aDepartureDateTime = a.departureDateTime;
        const bArrivalDateTime = b.arrivalDateTime;
  
        if (!aDepartureDateTime || !bArrivalDateTime) {
          console.error('Fecha de salida o llegada no definida');
          return 0;
        }
  
        // Convertir tanto la fecha y hora de salida como la de llegada a un formato comparable (milisegundos desde el epoch)
        const aDepartureTime = new Date(aDepartureDateTime).getTime();
        const bArrivalTime = new Date(bArrivalDateTime).getTime();
  
        return aDepartureTime - bArrivalTime;
      });
    });
  }
  
  flightsIsEmpty(): boolean {
    return this.showChooseFlight && this.outboundFlightsxc.destinationOptionList.length == 0;
  }
  
  proceedReservation(): void {
    this.proceeded = true;
    
    if (this.chooseFlightForm.valid) {
      this.router.navigateByUrl('/confirmation');
    }
  }
}
